import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress, Box, Typography, Paper } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

interface ClassOrderModalProps {
  open: boolean;
  loading: boolean;
  classOrder: string[];
  onClose: () => void;
  onSave: (newOrder: string[]) => void;
}

export const ClassOrderModal: React.FC<ClassOrderModalProps> = ({ open, loading, classOrder, onClose, onSave }) => {
  const [order, setOrder] = useState<string[]>(classOrder);

  React.useEffect(() => {
    setOrder(classOrder);
  }, [classOrder]);

  const moveUp = (idx: number) => {
    if (idx === 0) return;
    const newOrder = [...order];
    [newOrder[idx - 1], newOrder[idx]] = [newOrder[idx], newOrder[idx - 1]];
    setOrder(newOrder);
  };

  const moveDown = (idx: number) => {
    if (idx === order.length - 1) return;
    const newOrder = [...order];
    [newOrder[idx], newOrder[idx + 1]] = [newOrder[idx + 1], newOrder[idx]];
    setOrder(newOrder);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edit Class Order</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {order.map((className, idx) => (
              <Paper
                key={className}
                sx={(theme) => ({
                  width: '100%',
                  maxWidth: 600,
                  margin: '0 auto',
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  background: theme.palette.background.paper,
                  boxShadow: 1,
                })}
              >
                <Typography sx={(theme) => ({ color: theme.palette.text.primary, flex: 1 })}>
                  {className}
                </Typography>
                <Button
                  onClick={() => moveUp(idx)}
                  disabled={idx === 0}
                  size="small"
                  aria-label={`Move ${className} up`}
                >
                  <ArrowUpwardIcon fontSize="small" />
                </Button>
                <Button
                  onClick={() => moveDown(idx)}
                  disabled={idx === order.length - 1}
                  size="small"
                  aria-label={`Move ${className} down`}
                >
                  <ArrowDownwardIcon fontSize="small" />
                </Button>
              </Paper>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" variant="outlined">Cancel</Button>
        <Button onClick={() => onSave(order)} color="primary" variant="contained" disabled={loading}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};
